import React, { useState, useRef, useEffect } from 'react';
import type { HTMLAttributes } from 'react';

import clsx from 'clsx';
import ReactTruncatedComponent from 'react-truncated-component';

import { useId, useLocalizedStringFormatter } from '@coursera/cds-common';
import type { ForwardRefComponent } from '@coursera/cds-common';

import Button from '@core/Button';
import Typography from '@core/Typography2';

import getShowMoreTextCss, { classes } from './getShowMoreTextCss';
import i18nMessages from './i18n';

type ShowMoreTextType = ForwardRefComponent<HTMLDivElement, Props>;

// use same line-height as of `bodyPrimary` variant
// TODO: get value from CSS variable instead
const LINE_HEIGHT_BODY_PRIMARY = 24;

export type Props = {
  /**
   * Number of lines to show when in collapsed state.
   *
   * Per design recommendations, the minimum is `2` and max is `4`.
   * See https://www.figma.com/file/EkTjNxbpX7LbM6gwJdDdGQ?node-id=4118:17521&mode=design#608805377
   *
   * @default 3
   */
  collapsedNumLines?: 2 | 3 | 4;

  /**
   * The text content to be truncated.
   * Note that only text-like content should be passed here,
   * such as plain text, formatted text, or links.
   *
   * @example
   * <ShowMoreText>
   *   <p>
   *     First paragraph
   *   </p>
   *   <p>
   *     Second <a href="...">paragraph with link</a>
   *   </p>
   * </ShowMoreText>
   */
  children: React.ReactNode | React.ReactNode[];

  /**
   * Whether to render initially in expanded state
   *
   * @default false
   */
  defaultExpanded?: boolean;

  /**
   * Callback fired when the expanded state changes
   */
  onChange?: (expanded: boolean) => void;

  /**
   * Custom element to use for truncation when in collapsed state,
   * Uses the ellipses character "..." by default.
   *
   * @default &hellip; (ellipses character)
   */
  truncateElement?: React.ReactNode;

  /**
   * Set on the CTA button to call out a custom label for screen-readers.
   * Optional but highly recommended to be passed in since the default . If not, the CTA label
   * will be called out.
   *
   * @example
   * aria-label="Show more content about Machine Learning Specialization"
   */
  'aria-label'?: string;

  /**
   * Pass a custom lineHeight in pixels for the truncation logic.
   *
   * @default 24 line height for `bodyPrimary` text
   */
  lineHeight?: number;
} & Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>;

/**
 * Show More Text partially hides text to provide a seamless way to display
 * lengthy or detailed information while maintaining a clean and organized layout.
 *
 * See [Props](__storybookUrl__/components-surfaces-show-more-text--default#props)
 *
 * @example
 *  1. Formatted text:
 *    ```tsx
 *    <ShowMoreText collapsedNumLines={2}>
 *     <p>Here is some <strong>content</strong> that needs to be truncated</p>
 *     <p>Here is some <a href="link">link</a></p>
 *     <p>Here is some content that needs to be truncated</p>
 *    </ShowMoreText>
 *   ```
 *
 *  2. Plain text:
 *    ```tsx
 *    <ShowMoreText collapsedNumLines={2}>
 *      Here is some content that needs to be truncated
 *      Here is some content that needs to be truncated
 *      Here is some content that needs to be truncated
 *    </ShowMoreText>
 *   ```
 */
const ShowMoreText: ShowMoreTextType = React.forwardRef<HTMLDivElement, Props>(
  function ShowMoreText(
    {
      collapsedNumLines = 3,
      defaultExpanded = false,
      children,
      lineHeight = LINE_HEIGHT_BODY_PRIMARY,
      onChange,
      className,
      'aria-label': ariaLabel,
      // use special Ellipses character instead of '...' for semantics -
      // https://tosbourn.com/the-importance-of-using-the-ellipsis-character/
      // eslint-disable-next-line react/jsx-no-literals
      truncateElement = <span>&hellip;</span>,
      ...restProps
    },
    ref
  ) {
    const baseId = useId();
    const contentId = `${baseId}-content`;
    const contentRef = useRef<HTMLDivElement>(null);
    // keep track of a keyboard interaction with the CTA
    // so we can manually manage focus, see handleToggle()
    const isKeyboardEvent = useRef<boolean>(false);
    const stringFormatter = useLocalizedStringFormatter(i18nMessages);

    const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
    const [mounted, setMounted] = useState<boolean>(false);
    const [showButton, setShowButton] = useState<boolean>(defaultExpanded);
    const [keyboardFocus, setKeyboardFocus] = useState<boolean>(false);

    useEffect(() => {
      // only do truncation on mount,
      // for SSR or SEO cases we will show the full text
      if (!mounted) {
        setMounted(true);
      }

      if (expanded) {
        // manually set focus back on the full content
        // for screenreaders to read out.
        contentRef.current?.focus();
      }
    }, [expanded, contentRef, mounted]);

    const handleToggle = () => {
      setExpanded(!expanded);
      onChange?.(!expanded);

      if (!expanded && isKeyboardEvent.current) {
        setKeyboardFocus(true);
      } else {
        isKeyboardEvent.current = false;
        setKeyboardFocus(false);
      }
    };

    return (
      <div
        ref={ref}
        id={baseId}
        {...restProps}
        className={clsx(className, classes.base)}
        css={getShowMoreTextCss}
      >
        <Typography
          ref={contentRef}
          className={clsx(classes.content, {
            [classes.focusVisible]: keyboardFocus,
          })}
          component="div"
          id={contentId}
          // focus is programmatically controlled in useEffect()
          tabIndex={expanded ? -1 : undefined}
        >
          <ReactTruncatedComponent
            ellipsis={truncateElement}
            lineHeight={lineHeight}
            // disable truncation when expanded or not yet mounted
            numberOfLines={expanded || !mounted ? null : collapsedNumLines}
            onTruncate={(isTruncated) => setShowButton(isTruncated)}
          >
            {typeof children !== 'string' && children}

            {/* wrap plain-text strings in a DIV for correct truncation */}
            {typeof children === 'string' && <div>{children}</div>}
          </ReactTruncatedComponent>
        </Typography>

        {mounted && showButton && (
          <Button
            aria-controls={contentId}
            aria-expanded={expanded}
            aria-label={ariaLabel}
            className={classes.cta}
            edgeAlign="start"
            variant="ghost"
            onClick={handleToggle}
            onKeyDown={() => {
              isKeyboardEvent.current = true;
            }}
          >
            {expanded
              ? stringFormatter.format('show_less')
              : stringFormatter.format('show_more')}
          </Button>
        )}
      </div>
    );
  }
);

export default ShowMoreText;
